// This hook assumes one script per mount. main.tsx guarantees that by rendering
// the editor route with key={id}.

import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi, ApiError } from '../../lib/api';
import { createLatestSaveQueue, type LatestSaveOutcome } from '../../lib/latestSaveQueue';
import { serializeScratch, type ScratchDoc } from '../../../shared/scratch';

// Shorter than the script's 1500ms: scratch edits are small, often a single
// jot, and there is no diff/version machinery riding on them.
const SCRATCH_DEBOUNCE_MS = 800;

export type ScratchSaveState = 'idle' | 'saving' | 'saved' | 'error' | 'stale';

interface Options {
  scriptId: string;
  initialVersion: number;
}

/**
 * Autosave for the scratchpad. Mirrors useAutosave but is deliberately its own
 * queue, its own version counter and its own status: a stale scratch save must
 * never block a script save, or vice versa (see the plan, 1.2 Autosave).
 *
 * Going stale stops further attempts rather than overwriting whatever the other
 * tab wrote. The pane surfaces a reload notice instead.
 */
export function useScratchAutosave({ scriptId, initialVersion }: Options) {
  const [state, setState] = useState<ScratchSaveState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const versionRef = useRef(initialVersion);
  const staleRef = useRef(false);
  const keepaliveRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveAttemptRef = useRef<(doc: ScratchDoc) => Promise<LatestSaveOutcome>>(
    async () => 'retry',
  );
  const queueRef = useRef(
    createLatestSaveQueue((doc: ScratchDoc) => saveAttemptRef.current(doc)),
  );

  saveAttemptRef.current = async (doc: ScratchDoc): Promise<LatestSaveOutcome> => {
    const scratchJson = serializeScratch(doc);
    const lastChance = keepaliveRef.current;
    const body = { base_version: versionRef.current, scratch_json: scratchJson };
    // A keepalive fetch rejects outright once the origin's in-flight keepalive
    // bytes pass 64 KiB, so measure the actual wire body (JSON re-escapes
    // every quote in the ProseMirror JSON, so the inner length understates it).
    const bodyBytes = new TextEncoder().encode(JSON.stringify(body)).byteLength;
    try {
      const result = await adminApi.saveScratch(scriptId, body, {
        keepalive: lastChance && bodyBytes < 60_000,
      });
      if (result.ok) {
        versionRef.current = Math.max(versionRef.current, result.version);
        return 'saved';
      }
      if (result.error === 'stale') {
        staleRef.current = true;
        setState('stale');
        // Drop the pending value: retrying would 409 on every keystroke, and
        // the user's answer to a stale pad is to reload, not to overwrite.
        return 'discard';
      }
      setState('error');
      return 'retry';
    } catch (err) {
      if (!lastChance) console.error('scratchpad save failed', err);
      setState('error');
      setErrorMsg(err instanceof ApiError && err.message ? err.message : null);
      return 'retry';
    }
  };

  useEffect(() => {
    if (initialVersion > versionRef.current) versionRef.current = initialVersion;
  }, [initialVersion]);

  function schedule(doc: ScratchDoc) {
    if (staleRef.current) return;
    queueRef.current.schedule(doc);
    setState('idle');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void flush();
    }, SCRATCH_DEBOUNCE_MS);
  }

  async function flush(): Promise<boolean> {
    if (staleRef.current) return false;
    if (!queueRef.current.hasWork()) return true;
    setState('saving');
    setErrorMsg(null);
    const saved = await queueRef.current.flush();
    if (saved) setState('saved');
    return saved;
  }

  // Last-chance save when the tab goes away. The script's own beforeunload
  // handler owns the "are you sure" prompt; this just gets the bytes out.
  useEffect(() => {
    function onBeforeUnload() {
      if (!queueRef.current.hasWork()) return;
      keepaliveRef.current = true;
      void flush().finally(() => {
        keepaliveRef.current = false;
      });
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (queueRef.current.hasWork()) void flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPendingWork = useCallback(() => queueRef.current.hasWork(), []);

  return { state, errorMsg, schedule, flush, hasPendingWork };
}
